<template>
	<div>
		<h5 class="text-h5 q-mt-lg q-mb-md">{{ $t(game_data.game_name) }}</h5>

		<q-collapsible
			class="shadow-2 q-pa-sm q-ma-xs rounded-border"
			icon="far fa-ballot-check"
			right-icon="far fa-ballot-check"
			:label="$t('how_to_create_the_game')"
		>
			<div class="q-ma-sm">
				<p>{{ $t('flashcards_instructions') }}</p>
				<p class="q-mt-lg">
					<em
						><strong>{{ `${$t('warning')}: ` }}</strong
						>{{ $t('create_game_before_save') }}</em
					>
				</p>
			</div>
		</q-collapsible>

		<div class="row">
			<div class="col-12 q-mb-md">
				<div class="row gutter-sm">
					<div class="col-12">
						<uploader
							class="q-mt-xs"
							content_type="images"
							@fileUploaded="imageUploaded"
							:multiple="false"
							:cropper="true"
							:aspect_ratio="1 / 1"
							:aspect_ratio_string="'1/1'"
						/>

						<q-list
							v-if="game_data.images.length"
							dense
							bordered
							padding
							separator
							class="q-mt-md"
						>
							<q-item v-for="(image, index) in game_data.images" :key="index">
								<q-item-tile class="q-mr-sm">
									<img style="width: 9rem;" :src="image.image_link_signed" />
								</q-item-tile>
								<q-item-main class="q-mx-sm">
									<q-field :count="200">
										<q-input
											v-model="image.description"
											:error="
												$v.game_data.images.$each[index].description.$error
											"
											@input="
												$v.game_data.images.$each[index].description.$touch
											"
											:float-label="$t('description')"
										/>
									</q-field>
									<q-field :count="80" class="q-mb-sm">
										<q-input
											v-model="image.answer"
											:error="$v.game_data.images.$each[index].answer.$error"
											@input="$v.game_data.images.$each[index].answer.$touch"
											:float-label="$t('correct_answer')"
										/>
									</q-field>
								</q-item-main>
								<q-item-side right>
									<q-icon
										v-if="image.image_link.length && image.answer.length"
										name="fal fa-check"
										color="positive"
									/>
									<q-btn
										round
										flat
										color="negative"
										icon="fal fa-trash"
										@click="removeFile(image)"
									>
										<q-tooltip>
											{{ $t('remove') }}
										</q-tooltip>
									</q-btn>
								</q-item-side>
							</q-item>
						</q-list>

						<div class="row justify-end q-mr-md q-mt-md">
							<q-btn
								color="secondary"
								:label="$t('preview')"
								@click="buildGamePreview"
								:disable="disableGamePreview"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>

		<flashcards-preview
			v-if="show_game_preview"
			:show_game_preview="show_game_preview"
			@hide_preview="hidePreview()"
			:game_name="game_data.game_name"
			:images="game_data.images"
		/>
	</div>
</template>

<script>
import Uploader from 'components/Uploader'
import FlashcardsPreview from 'components/admin/games/FlashcardsPreview'
import { required, minLength, maxLength } from 'vuelidate/lib/validators'
import slugify from '../../../functions/slugify.js'

export default {
	name: 'AdminGameFlashcards',

	props: ['can_save', 'saved_game_data', 'id_game'],

	data() {
		return {
			image_field: null,
			game_data: {
				game_name: 'flashcards',
				images: [],
			},
			show_game_preview: false,
			game_build: false,
		}
	},

	components: {
		uploader: Uploader,
		'flashcards-preview': FlashcardsPreview,
	},

	computed: {
		disableGamePreview() {
			if (this.game_data.images.length < 1) return true
			else {
				return this.game_data.images.findIndex((e) => e.answer === '') !== -1 ? true : false
			}
		},
	},

	methods: {
		addImageField(url, url_signed) {
			let newField = {
				image_link: url,
				image_link_signed: url_signed || url,
				description: '',
				answer: '',
				user_answer: '',
				tip: '',
				correct: null,
			}

			this.game_data.images.push(newField)
			this.resetGame()
		},

		imageUploaded(url, item, urlSigned) {
			if (url) this.addImageField(url, urlSigned)
		},

		removeFile(img) {
			if (this.game_data.images.length <= 1) this.game_data.images = []
			else
				this.game_data.images = this.game_data.images.filter(
					(e) => e.image_link !== img.image_link
				)
			this.resetGame()
		},

		buildGamePreview() {
			this.game_data.images = this.game_data.images.map((e) => ({
				...e,
				// image_link: e.image_link_signed,
				answer: e.answer.trim(),
				slug: slugify(e.answer.trim()),
				tip: e.answer.substring(0, 3),
			}))

			this.show_game_preview = true
			this.game_built = true

			this.$v.$touch()
			if (!this.$v.$error) {
				this.game_built = true
				this.game_data.images = this.game_data.images.map((img) => ({
					...img,
					image_link: img.image_link.split('?response')[0],
				}))
				this.$emit('save_toggle', this.game_built)
				this.$emit('save_game_data', this.game_data)
			}
		},

		resetGame() {
			this.game_built = false
			this.$emit('save_toggle', this.game_built)
			this.$emit('save_game_data', null)
		},

		hidePreview() {
			this.show_game_preview = false
		},
	},

	mounted() {
		if (this.saved_game_data) {
			this.game_data = this.saved_game_data
			this.game_data.images = this.game_data.images.map((img) => ({
				...img,
				image_link_signed: img.image_link,
			}))
		} else {
			this.game_data = {
				game_name: 'flashcards',
				images: [],
			}
		}
	},

	beforeDestroy() {
		this.resetGame()
	},

	validations() {
		return {
			game_data: {
				images: {
					$each: {
						description: { required, maxLength: maxLength(200) },
						answer: { required, maxLength: maxLength(80) },
					},
				},
			},
		}
	},
}
</script>

<style></style>
